// client/src/store/SocketContext.js
import React, { createContext, useContext, useEffect, useState } from "react";
import { Context } from "../index";
import { observer } from "mobx-react-lite";
import { getAuthIdentity, getFreshAccessToken } from "../http/index";

const SocketContext = createContext(null);
export const useSocket = () => useContext(SocketContext);

const normalizeUrl = (url) => String(url || "").replace(/\/+$/, "");

// helper: подождать idle/тик — чтобы не мешать первому рендеру
function scheduleAfterPaint(cb) {
    if (typeof window === "undefined") return;
    if ("requestIdleCallback" in window) {
        const id = window.requestIdleCallback(() => cb(), { timeout: 1500 });
        return () => window.cancelIdleCallback(id);
    }
    const t = setTimeout(cb, 300);
    return () => clearTimeout(t);
}

export const SocketProvider = observer(({ children }) => {
    const { user } = useContext(Context);
    // Read MobX values during render so observer tracks the connection identity.
    const isLoaded = user.isLoaded;
    const isAuth = user.isAuth;
    const currentUserId = isAuth ? user.UserData?.id ?? null : null;
    const [socket, setSocket] = useState(null);
    const [guestSessionVersion, setGuestSessionVersion] = useState(0);

    useEffect(() => {
        const handleGuestSessionReady = () => {
            setGuestSessionVersion(value => value + 1);
        };

        window.addEventListener('chat:guest-session-ready', handleGuestSessionReady);

        return () => {
            window.removeEventListener('chat:guest-session-ready', handleGuestSessionReady);
        };
    }, []);

    useEffect(() => {
        if (!isLoaded || (isAuth && !currentUserId)) {
            return;
        }

        // Backend resolves guest identity from the handshake cookie.
        // If the cookie appears later, chat:guest-session-ready reconnects us.
        let isCancelled = false;
        let disconnectScheduler = null;
        let newSocket = null;
        let authRetryTimer = null;

        const SOCKET_URL =
            normalizeUrl(process.env.REACT_APP_API_URL) || "http://localhost:5000";

        const isCurrentIdentity = () => !isCancelled && user.isAuth === isAuth &&
            (!isAuth || String(user.UserData?.id) === String(currentUserId));

        const connect = async () => {
            console.log("🌐 Подключаем сокет (Auth:", isAuth, ")");

            const mod = await import("socket.io-client");
            const io = mod.io ?? mod.default ?? mod;

            if (isCancelled) return;

            newSocket = io(SOCKET_URL, {
                withCredentials: true,
                autoConnect: false,
                transports: ["websocket", "polling"],
                auth: callback => {
                    if (!isCurrentIdentity()) return;
                    if (!isAuth) { callback({}); return; }
                    getFreshAccessToken().then(token => {
                        if (!isCurrentIdentity()) return;
                        if (getAuthIdentity() !== `user:${currentUserId}`) {
                            throw new Error("Socket account changed");
                        }
                        callback({ token });
                    }).catch(error => {
                        if (!isCurrentIdentity()) return;
                        // Never silently connect as a guest with an expired JWT.
                        console.warn("Socket auth refresh failed:", error?.message);
                        newSocket.disconnect();
                        if (authRetryTimer) window.clearTimeout(authRetryTimer);
                        authRetryTimer = window.setTimeout(() => {
                            authRetryTimer = null;
                            if (isCurrentIdentity()) newSocket.connect();
                        }, 5000);
                    });
                },
            });

            newSocket.on("connect", () => {
                console.log("✅ SOCKET CONNECTED:", newSocket.id);
            });

            newSocket.on("connect_error", (err) => {
                console.warn("⚠️ SOCKET ERROR:", err.message);
            });

            if (!isCancelled) {
                setSocket(newSocket);
                newSocket.connect();
            }
        };

        disconnectScheduler = scheduleAfterPaint(() => {
            connect().catch((e) => console.warn("⚠️ SOCKET INIT FAILED:", e?.message));
        });

        return () => {
            isCancelled = true;
            if (disconnectScheduler) disconnectScheduler();
            if (authRetryTimer) window.clearTimeout(authRetryTimer);

            if (newSocket) {
                console.log("🟡 SOCKET DISCONNECTING");
                newSocket.close();
            }

            setSocket(null);
        };
    }, [isLoaded, isAuth, currentUserId, guestSessionVersion]);

    return (
        <SocketContext.Provider value={socket}>{children}</SocketContext.Provider>
    );
});
